/* eslint-disable */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Redirect } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
	makeStyles,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import {
	Page,
	PageContent,
	PageHeaderAddButton,
	PageHeaderListActions,
	PopupSnackbar,
	ScrollToTopOnMount,
} from "frame/components";
import { safetyWiseApi } from "utils/safetyWiseApi";
import { onList as onStaffListRequested } from "features/staff/actions";
import { onListRequested as onSitesListRequested } from "features/sites/actions";
import { canViewAllTasks, getTasksNavTitle, isStaffUser } from "utils";
import { HOME_ROUTE } from "features/home/home-routes";
import { getTaskStatusLabel, getTaskStatusSortIndex } from "../task-status";
import {
	buildTaskStaffFilterOptions,
	isActiveStaff,
	mapStaffToOption,
} from "../task-staff-filter";
import ScheduleEntryDialog from "./schedule-entry-dialog";
import TaskListing from "./task-listing";

const useStyles = makeStyles((theme) => ({
	body: {
		marginTop: theme.spacing(2),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3),
		},
	},
	staffFilter: {
		minWidth: 220,
	},
	muted: {
		color: theme.palette.text.secondary,
	},
	gridContainer: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3),
		},
	},
	filterGrid: {
		paddingTop: theme.spacing(1),
	},
	search: {
		display: "flex",
	},
}));

const getCurrentOrganisationId = (currentOrganisation) => {
	if (!currentOrganisation) return "";
	if (typeof currentOrganisation === "string") return currentOrganisation;
	return currentOrganisation.id || "";
};

const getMySchedule = async (organisationId, fromUtc, toUtc) => {
	const query = `fromUtc=${encodeURIComponent(fromUtc)}&toUtc=${encodeURIComponent(toUtc)}`;
	return safetyWiseApi.get(`${organisationId}/schedule/my?${query}`);
};

const getScheduleRange = async (organisationId, fromUtc, toUtc, staffId) => {
	return safetyWiseApi.post(`${organisationId}/schedule/range`, {
		fromUTC: fromUtc,
		toUTC: toUtc,
		staffId: staffId || null,
	});
};

const sortTasks = (items) =>
	[...items].sort((a, b) => {
		const statusDiff = getTaskStatusSortIndex(a.status) - getTaskStatusSortIndex(b.status);
		if (statusDiff !== 0) return statusDiff;
		return (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" });
	});

const taskMatchesSearch = (item, rawTerm) => {
	const term = (rawTerm || "").trim().toLowerCase();
	if (!term) return true;
	const haystacks = [
		item.title,
		item.notes,
		item.siteName,
		item.staffFullName,
		item.customerName,
		item.status,
		getTaskStatusLabel(item.status),
	];
	return haystacks.some((value) => value && String(value).toLowerCase().includes(term));
};

/** Wide range so tasks load without date filter UI (API still requires from/to). */
const TASK_LIST_FROM_UTC = moment().subtract(1, "year").startOf("day").utc().toISOString();
const TASK_LIST_TO_UTC = moment().add(1, "year").endOf("day").utc().toISOString();

const isActiveSite = (site) =>
	site?.status && String(site.status).toLowerCase() === "active";

export const MySchedulePage = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const currentOrganisation = useSelector(({ organisations }) => organisations.currentOrganisation);
	const role = useSelector(({ profile }) => profile.role);
	const staffId = useSelector(({ profile }) => profile.staffId);
	const staffResults = useSelector(({ staff }) => staff.pagination?.results || []);
	const siteResults = useSelector(({ sites }) => sites.pagination?.results || []);
	const organisationId = getCurrentOrganisationId(currentOrganisation);
	const tasksEnabled = useSelector(({ featureFlags }) => featureFlags?.tasks === true);
	const viewAllTasks = canViewAllTasks(role);
	const pageTitle = getTasksNavTitle(role);
	const isStaff = isStaffUser(role);

	const [items, setItems] = useState([]);
	const [selectedStaffId, setSelectedStaffId] = useState("");
	const [loading, setLoading] = useState(false);
	const [saveSuccessMessage, setSaveSuccessMessage] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [taskDialogOpen, setTaskDialogOpen] = useState(false);
	const [editingEntry, setEditingEntry] = useState(null);
	const [searchFilter, setSearchFilter] = useState("");
	const [showFilters, setShowFilters] = useState(false);

	const allStaffOptions = useMemo(
		() => buildTaskStaffFilterOptions(staffResults),
		[staffResults]
	);

	const activeStaffOptions = useMemo(() => {
		const includeStaffId = editingEntry?.staffId;
		return staffResults
			.filter((s) => isActiveStaff(s) || (includeStaffId && s.id === includeStaffId))
			.map(mapStaffToOption);
	}, [staffResults, editingEntry?.staffId]);

	const staffOptions = useMemo(() => {
		if (viewAllTasks) return activeStaffOptions;
		if (!staffId) return [];
		const match = activeStaffOptions.find((s) => s.id === staffId);
		return match ? [match] : [{ id: staffId, name: "Me" }];
	}, [activeStaffOptions, staffId, viewAllTasks]);

	const siteOptions = useMemo(() => {
		const includeSiteId = editingEntry?.siteId;
		return siteResults
			.filter((s) => isActiveSite(s) || (includeSiteId && s.id === includeSiteId))
			.map((s) => ({
				id: s.id,
				name: s.siteName || "Unnamed site",
			}));
	}, [siteResults, editingEntry?.siteId]);

	const defaultDialogStaffId = viewAllTasks
		? selectedStaffId || activeStaffOptions[0]?.id || staffId || ""
		: staffId || "";

	const loadTasks = useCallback(async () => {
		if (!organisationId) return;
		setLoading(true);
		try {
			const response = viewAllTasks
				? await getScheduleRange(
						organisationId,
						TASK_LIST_FROM_UTC,
						TASK_LIST_TO_UTC,
						selectedStaffId || null
					)
				: await getMySchedule(organisationId, TASK_LIST_FROM_UTC, TASK_LIST_TO_UTC);
			setItems(Array.isArray(response.data) ? response.data : []);
		} catch (e) {
			setErrorMessage("Failed to load tasks.");
		} finally {
			setLoading(false);
		}
	}, [organisationId, selectedStaffId, viewAllTasks]);

	useEffect(() => {
		if (organisationId) {
			dispatch(onStaffListRequested());
			dispatch(onSitesListRequested());
		}
	}, [dispatch, organisationId]);

	useEffect(() => {
		loadTasks();
	}, [loadTasks]);

	useEffect(() => {
		if (!selectedStaffId || staffResults.length === 0) return;
		const selected = staffResults.find((staff) => staff.id === selectedStaffId);
		if (selected && !isActiveStaff(selected)) {
			setSelectedStaffId("");
		}
	}, [staffResults, selectedStaffId]);

	const tasks = useMemo(() => {
		const sorted = sortTasks(items);
		if (!searchFilter.trim()) return sorted;
		return sorted.filter((item) => taskMatchesSearch(item, searchFilter));
	}, [items, searchFilter]);

	const openCreateTask = () => {
		setEditingEntry(null);
		setTaskDialogOpen(true);
	};

	const openEditTask = (entry) => {
		setEditingEntry(entry);
		setTaskDialogOpen(true);
	};

	const closeTaskDialog = () => {
		setTaskDialogOpen(false);
		setEditingEntry(null);
	};

	const handleNotify = ({ message, severity }) => {
		if (severity === "success") {
			setSaveSuccessMessage(message);
		} else {
			setErrorMessage(message);
		}
	};

	const canCreateTask = viewAllTasks ? activeStaffOptions.length > 0 : !!staffId;

	if (!tasksEnabled) {
		return <Redirect to={HOME_ROUTE} />;
	}

	return (
		<Page title={pageTitle}>
			<ScrollToTopOnMount />
			{saveSuccessMessage && (
				<PopupSnackbar
					message={saveSuccessMessage}
					coloured
					success
					onExited={() => setSaveSuccessMessage("")}
				/>
			)}
			{errorMessage && (
				<PopupSnackbar
					message={errorMessage}
					coloured
					error
					onExited={() => setErrorMessage("")}
				/>
			)}
			<PageHeaderAddButton
				title={pageTitle}
				addTabTitle="New"
				onAdd={canCreateTask ? openCreateTask : () => {}}
				staffUserHide={false}
				titleAdornment={
					<PageHeaderListActions
						showFilters={showFilters}
						onToggleFilters={() => setShowFilters(!showFilters)}
					/>
				}
			>
				{showFilters && (
					<div className={classes.gridContainer}>
						<Grid container spacing={2} className={classes.filterGrid}>
							{viewAllTasks && (
								<Grid item xs={12} md={6}>
									<FormControl
										variant="outlined"
										size="small"
										className={classes.staffFilter}
										style={{ backgroundColor: "white", maxWidth: 480, width: "100%" }}
									>
										<InputLabel id="tasks-staff-filter">Staff</InputLabel>
										<Select
											labelId="tasks-staff-filter"
											value={selectedStaffId}
											onChange={(e) => setSelectedStaffId(e.target.value)}
											label="Staff"
										>
											<MenuItem value="">All Staff</MenuItem>
											{allStaffOptions.map((staff) => (
												<MenuItem key={staff.id} value={staff.id}>
													{staff.name}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>
							)}
							<Grid item xs={12} md={6}>
								<TextField
									className={classes.search}
									id="my-tasks-search"
									variant="outlined"
									label="Search"
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<SearchIcon />
											</InputAdornment>
										),
										endAdornment: searchFilter && (
											<InputAdornment position="end">
												<IconButton
													aria-label="clear search"
													onClick={() => setSearchFilter("")}
													edge="end"
													size="small"
												>
													<ClearIcon />
												</IconButton>
											</InputAdornment>
										),
									}}
									value={searchFilter}
									onChange={(e) => setSearchFilter(e.target.value)}
									style={{ backgroundColor: "white", maxWidth: 480, width: "100%" }}
									placeholder="e.g. Title, Notes, Site, Staff, Status"
								/>
							</Grid>
						</Grid>
					</div>
				)}
			</PageHeaderAddButton>
			<PageContent pad>
				<div className={classes.body}>
					{loading && <Typography className={classes.muted}>Loading tasks...</Typography>}
					{!loading && items.length === 0 && (
						<Typography className={classes.muted}>No tasks yet.</Typography>
					)}
					{!loading && items.length > 0 && tasks.length === 0 && (
						<Typography className={classes.muted}>No tasks match your search.</Typography>
					)}

					{tasks.map((item) => (
						<TaskListing
							key={item.id}
							task={item}
							showStaff={viewAllTasks}
							staffList={staffResults}
							onEdit={openEditTask}
						/>
					))}
				</div>
			</PageContent>

			<ScheduleEntryDialog
				simpleMode
				open={taskDialogOpen}
				onClose={closeTaskDialog}
				organisationId={organisationId}
				entry={editingEntry}
				defaultStaffId={defaultDialogStaffId}
				staffLocked={isStaff}
				staffOptions={staffOptions}
				siteOptions={siteOptions}
				onSaved={loadTasks}
				onNotify={handleNotify}
				createTitle="Add Task"
				editTitle="Edit Task"
				saveSuccessMessage="Task saved successfully"
				cancelSuccessMessage="Task cancelled."
				cancelReason={viewAllTasks ? "Cancelled from Tasks" : "Cancelled from My Tasks"}
			/>
		</Page>
	);
};

export default MySchedulePage;
